import { useEffect, useRef, useState } from "react"

const DEFAULT_CONFIRMATION_DURATION = 1600

/**
 * Briefly reports an explicitly confirmed form submission. Dirtiness still
 * clears the confirmation when editing resumes, but it is never used to infer
 * whether the request succeeded.
 */
export default function useFormSubmissionConfirmation({
  dirty,
  enabled = true,
  isSubmitting,
  successfulSubmissionCount,
  duration = DEFAULT_CONFIRMATION_DURATION
}: {
  dirty: boolean
  enabled?: boolean
  isSubmitting: boolean
  successfulSubmissionCount: number
  duration?: number
}) {
  const [didSave, setDidSave] = useState(false)
  const previousSuccessCount = useRef(successfulSubmissionCount)

  useEffect(() => {
    if (!enabled) {
      previousSuccessCount.current = successfulSubmissionCount
      setDidSave(false)
      return
    }

    if (isSubmitting) {
      setDidSave(false)
      return
    }

    if (successfulSubmissionCount === previousSuccessCount.current) return

    previousSuccessCount.current = successfulSubmissionCount
    if (!dirty) setDidSave(true)
  }, [dirty, enabled, isSubmitting, successfulSubmissionCount])

  useEffect(() => {
    if (!didSave) return

    const confirmationTimer = setTimeout(
      () => setDidSave(false),
      Math.max(0, duration)
    )
    return () => clearTimeout(confirmationTimer)
  }, [didSave, duration])

  useEffect(() => {
    if (dirty) setDidSave(false)
  }, [dirty])

  return didSave
}
